"use client";

import { useState } from "react";
import { ChevronDown, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { rijderHref } from "@/lib/secties";
import { UitslagItem } from "@/components/wedstrijd-uitslag";
import type { UitslagBaan, UitslagOpBaan } from "@/server/uitslagen";

// De uitslag in de volgorde waarin de dag verliep: per baan, op tijd.
//
// Dezelfde indeling als de startlijst op de wedstrijdpagina, zodat je een dag
// vooraf en achteraf op dezelfde manier leest. Wie erbij was herinnert zich
// "baan 1, kwart over drie" - niet "Farm Stock, sectie 2".
export function UitslagBanen({ banen }: { banen: UitslagBaan[] }) {
  const [actief, setActief] = useState(banen[0]?.nummer ?? 1);
  const [open, setOpen] = useState<string | null>(null);

  const baan = banen.find((b) => b.nummer === actief) ?? banen[0];
  if (!baan) return null;

  return (
    <div className="py-5">
      {banen.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {banen.map((b) => (
            <button
              key={b.nummer}
              type="button"
              onClick={() => {
                setActief(b.nummer);
                setOpen(null);
              }}
              aria-pressed={b.nummer === actief}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 font-display text-sm font-black uppercase tracking-wide transition-colors",
                b.nummer === actief
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-white/70 hover:text-white",
              )}
            >
              Baan {b.nummer}
            </button>
          ))}
        </div>
      )}

      <p className="mb-3 px-4 text-xs text-muted-foreground">
        {baan.klassen.length} {baan.klassen.length === 1 ? "onderdeel" : "onderdelen"}, in de
        volgorde van het programma
      </p>

      <ol className="space-y-2 px-4">
        {baan.klassen.map((k) => (
          <li key={k.key}>
            <Onderdeel
              klasse={k}
              open={open === k.key}
              onWissel={() => setOpen(open === k.key ? null : k.key)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function Onderdeel({
  klasse,
  open,
  onWissel,
}: {
  klasse: UitslagOpBaan;
  open: boolean;
  onWissel: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onWissel}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-3 text-left"
      >
        <span className="flex w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-muted py-1.5">
          {klasse.tijd ? (
            <span className="font-display text-base font-black leading-none tabular-nums text-primary">
              {klasse.tijd}
            </span>
          ) : (
            <Clock className="size-4 text-white/30" aria-hidden />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block font-display text-sm font-bold uppercase leading-snug tracking-wide text-white">
            {klasse.onderdeel}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {klasse.deelnemers.length}{" "}
            {klasse.deelnemers.length === 1 ? "deelnemer" : "deelnemers"}
          </span>
        </span>

        <ChevronDown
          className={cn(
            "size-5 shrink-0 text-primary transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open && (
        <ul className="divide-y divide-white/5 border-t border-white/5">
          {klasse.deelnemers.map((d, i) => (
            <li key={i}>
              <UitslagItem
                d={d}
                href={rijderHref(klasse.sectieCode, d.deelnID)}
                sectie={klasse.sectieCode}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
